import { useEffect, useMemo, useState } from "react";

import { useMedplum } from "@medplum/react";

import { fetchIsLabOrdersIntegrationActive } from "@/health-gorilla";
import { resolveDestinationState, useDestinationAccess } from "@/navigation";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { SETTINGS_NAV_LINKS } from "./constants";
import { SettingsNavLink } from "./types";

/**
 * The settings sections this user sees, in order, each marked disabled when its gate applies.
 * The settings layout calls it once and hands the links to the sidebar and the phone section
 * select, so they always list the same sections.
 *
 * @returns The visible settings links with their disabled state.
 */
export function useSettingsNavLinks(): SettingsNavLink[] {
  const medplum = useMedplum();
  const baseAccess = useDestinationAccess();
  const { practiceId } = baseAccess;
  const [labsLookup, setLabsLookup] = useState<LabsLookup>();
  // A lookup answers for the practice it ran against. Until one answers for the active practice,
  // the integration is unknown, so Lab Order Templates stays disabled.
  const isLabOrdersIntegrationActive =
    labsLookup?.practiceId === practiceId ? labsLookup.isActive : undefined;

  useEffect(() => {
    let isCurrent = true;
    // A failed lookup leaves Lab Order Templates disabled. Medplum errors can quote server data,
    // so the report carries only the practice id.
    fetchIsLabOrdersIntegrationActive(medplum, practiceId)
      .then((isActive) => {
        if (isCurrent) setLabsLookup({ practiceId, isActive });
      })
      .catch((error: unknown) =>
        captureExceptionWithoutPhi("Settings lab integration lookup failed", error, {
          practiceId,
        }),
      );
    return () => {
      isCurrent = false;
    };
  }, [medplum, practiceId]);

  return useMemo(() => {
    const access = { ...baseAccess, isLabOrdersIntegrationActive };
    return SETTINGS_NAV_LINKS.reduce<SettingsNavLink[]>(
      (links, { label, Icon, to, end, section, gates }) => {
        const state = resolveDestinationState(gates, access);
        if (state === "hidden") return links;
        links.push({ label, Icon, to, end, section, isDisabled: state === "disabled" });
        return links;
      },
      [],
    );
  }, [baseAccess, isLabOrdersIntegrationActive]);
}

/** A lab-integration answer and the practice it was looked up for. */
type LabsLookup = { practiceId: string; isActive: boolean };
